// FormacionManager
//
// Gestor completo de una pagina de Formacion en el panel admin: videos, guias
// PDF y los bloques de informacion (uno por audiencia), con wizard de subida y
// drawer de edicion.
//
// Es el mismo componente para las dos verticales. Lo que cambia se inyecta por
// props:
//   - `api`              cliente HTTP (ReviResourceAPI | TrainingResourceAPI)
//   - `audienceOptions`  [{ value, label }] destinatarios de esa vertical
//   - `infoBlocks`       textos de cada bloque de informacion
// Los DATOS nunca se cruzan: cada api apunta a su propia coleccion.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Segmented, Space, Typography, message } from "antd";
import { PlayCircleOutlined, FilePdfOutlined } from "@ant-design/icons";
import {
  ResourceSection,
  ResourceUploadWizard,
  ResourceEditDrawer,
  InfoEditor,
} from "./_shared";
import styles from "./FormacionManager.module.scss";

const PAGE_SIZE = 12;

function useResourceCollection(api, token, type, audience, reloadTick) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cambiar de filtro debe volver a la primera pagina.
  useEffect(() => {
    setPage(1);
  }, [audience]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) return;
      try {
        setLoading(true);
        setError(null);
        const res = await api.list(token, {
          page,
          limit: PAGE_SIZE,
          type,
          audience,
        });
        if (alive) {
          setItems(res.resources || []);
          setTotal(res.pagination?.totalDocs || 0);
        }
      } catch (e) {
        if (alive) setError(e?.body?.message || e?.message || "Error");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [api, token, type, audience, page, reloadTick]);

  return { items, page, total, loading, error, setPage };
}

export function FormacionManager({
  api,
  accessToken,
  audienceOptions = [],
  infoBlocks = [],
  videosLabel = "Vídeos",
  pdfsLabel = "Guías (PDF)",
}) {
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  // Filtro "ver como": permite comprobar exactamente que material recibe cada
  // perfil sin tener que entrar a su portal.
  const [audienceFilter, setAudienceFilter] = useState("all");
  const audienceParam = audienceFilter === "all" ? undefined : audienceFilter;

  const filterOptions = useMemo(
    () => [
      { value: "all", label: "Todo" },
      ...audienceOptions.map((o) => ({ value: o.value, label: o.label })),
    ],
    [audienceOptions],
  );

  const videos = useResourceCollection(
    api,
    accessToken,
    "video",
    audienceParam,
    reloadTick,
  );
  const pdfs = useResourceCollection(
    api,
    accessToken,
    "pdf",
    audienceParam,
    reloadTick,
  );

  // Bloques de informacion: uno por audiencia, cada uno con su propio registro
  // (type=info + audiencia). Se cargan siempre, al margen del filtro de arriba.
  const [infos, setInfos] = useState({});
  const [infoLoading, setInfoLoading] = useState(false);
  const [infoError, setInfoError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!accessToken || !infoBlocks.length) return;
      try {
        setInfoLoading(true);
        setInfoError(null);
        const entries = await Promise.all(
          infoBlocks.map(async (block) => {
            const res = await api.list(accessToken, {
              page: 1,
              limit: 1,
              type: "info",
              audience: block.audience,
            });
            return [block.audience, res.resources?.[0] || null];
          }),
        );
        if (alive) setInfos(Object.fromEntries(entries));
      } catch (e) {
        if (alive) setInfoError(e?.body?.message || e?.message || "Error");
      } finally {
        if (alive) setInfoLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [api, accessToken, infoBlocks, reloadTick]);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardType, setWizardType] = useState("video");
  const [editing, setEditing] = useState(null);

  const onCreateVideo = useCallback(() => {
    setWizardType("video");
    setWizardOpen(true);
  }, []);
  const onCreatePdf = useCallback(() => {
    setWizardType("pdf");
    setWizardOpen(true);
  }, []);

  const onEdit = useCallback((resource) => setEditing(resource), []);
  const onDelete = useCallback(
    async (resource) => {
      try {
        await api.remove(accessToken, resource._id);
        message.success(`"${resource.title}" borrado`);
        reload();
      } catch (e) {
        message.error(e?.body?.message || e?.message || "Error al borrar");
      }
    },
    [api, accessToken, reload],
  );

  // Cuando se filtra por una audiencia concreta, el wizard la propone marcada.
  // Memoizado para no darle al wizard un array nuevo en cada render.
  const defaultAudiences = useMemo(
    () =>
      audienceFilter === "all"
        ? audienceOptions.slice(0, 1).map((o) => o.value)
        : [audienceFilter],
    [audienceFilter, audienceOptions],
  );

  return (
    <div className={styles.pageFormacion}>
      {audienceOptions.length ? (
        <Card size="small">
          <Space align="center" wrap>
            <Typography.Text strong>Ver contenido de:</Typography.Text>
            <Segmented
              options={filterOptions}
              value={audienceFilter}
              onChange={setAudienceFilter}
            />
            <Typography.Text type="secondary">
              Filtra la vista para comprobar qué recibe cada perfil.
            </Typography.Text>
          </Space>
        </Card>
      ) : null}

      <ResourceSection
        title={videosLabel}
        icon={<PlayCircleOutlined />}
        resources={videos.items}
        loading={videos.loading}
        error={videos.error}
        total={videos.total}
        page={videos.page}
        pageSize={PAGE_SIZE}
        onPageChange={videos.setPage}
        onCreate={onCreateVideo}
        onEdit={onEdit}
        onDelete={onDelete}
        ctaLabel="Nuevo vídeo"
        audienceOptions={audienceOptions}
      />

      <ResourceSection
        title={pdfsLabel}
        icon={<FilePdfOutlined />}
        resources={pdfs.items}
        loading={pdfs.loading}
        error={pdfs.error}
        total={pdfs.total}
        page={pdfs.page}
        pageSize={PAGE_SIZE}
        onPageChange={pdfs.setPage}
        onCreate={onCreatePdf}
        onEdit={onEdit}
        onDelete={onDelete}
        ctaLabel="Nueva guía"
        audienceOptions={audienceOptions}
      />

      {infoBlocks.map((block) => (
        <InfoEditor
          key={block.audience}
          audience={block.audience}
          title={block.title}
          titlePlaceholder={block.titlePlaceholder}
          descriptionPlaceholder={block.descriptionPlaceholder}
          emptyText={block.emptyText}
          info={infos[block.audience] || null}
          loading={infoLoading}
          accessToken={accessToken}
          api={api}
          onSaved={reload}
        />
      ))}

      {infoError ? (
        <div style={{ color: "#c63f3e", fontSize: 13, padding: 8 }}>
          {infoError}
        </div>
      ) : null}

      <ResourceUploadWizard
        open={wizardOpen}
        initialType={wizardType}
        accessToken={accessToken}
        api={api}
        audienceOptions={audienceOptions}
        defaultAudiences={defaultAudiences}
        onClose={() => setWizardOpen(false)}
        onCreated={(created) => {
          if (created) {
            message.success(`"${created.title}" creado correctamente`);
          }
          reload();
        }}
      />

      <ResourceEditDrawer
        open={!!editing}
        resource={editing}
        accessToken={accessToken}
        api={api}
        audienceOptions={audienceOptions}
        onClose={() => setEditing(null)}
        onSaved={reload}
        onDeleted={reload}
      />
    </div>
  );
}
